/** 抽屉型的表单输入 FormField 容器 */
import React, { useCallback, useState, useEffect, cloneElement, useRef, ReactNode } from 'react';
import type { FC } from 'react';
import { Drawer } from '@tencent/calendar-web-component-mobile';
import { Form } from '@tencent/tea-component';
import './index.scss';
// eslint-disable-next-line import/no-webpack-loader-syntax

import classNames from 'classnames';

export interface IProps extends Pick<IDrawerFormFieldProps, 'title' | 'previewFmt' | 'children'> {
  /** form label */
  label: ReactNode;
  /** form field key */
  name: string;
  /** form field 样式 */
  className?: string;
  /** 抽屉样式 */
  drawerClassName?: string;
  /** FormItem layout */
  layout?: 'vertical' | 'horizontal';
  onChange?: Function;
  value?: any;
  /** 默认状态 */
  defalutVisible?: boolean;
  /** 是否变成搜索框样式 */
  isSearch?: boolean;
  /** 点击取消按钮后的回调 */
  removeSearchVal?: Function;
}
const FormField: FC<IProps> = ({
  title,
  name,
  label,
  layout,
  className,
  drawerClassName,
  defalutVisible = false,
  previewFmt,
  children,
  onChange,
  value,
  isSearch,
  removeSearchVal,
}) => {
  const [active, setActive] = useState(false);

  const show = useCallback(() => setActive(true), [setActive]);
  const hide = useCallback(() => setActive(false), [setActive]);
  useEffect(() => {
    setActive(defalutVisible);
  }, [defalutVisible]);
  return (
    <Form.Item
      onClick={show}
      name={name}
      label={label}
      layout={layout}
      className={classNames(className)}
    >
      <DrawerFormField
        visible={active}
        onClose={hide}
        title={title}
        previewFmt={previewFmt}
        className={drawerClassName}
        onChange={onChange}
        value={value}
        isSearch={isSearch}
        removeSearchVal={removeSearchVal}
      >
        {children}
      </DrawerFormField>
    </Form.Item>
  );
};

interface IDrawerFormFieldProps {
  isSearch?: any;
  value?: any;
  visible: boolean;
  /** 抽屉上的标题 */
  title?: string;
  className?: string;
  children: React.ReactElement;
  headerSlot?: ReactNode;
  /** 点击取消按钮后的回调 */
  removeSearchVal?(val: string): void;
  previewFmt?(value: any): string;
  onChange?(value: any): void;
  onClose(): void;
}

const defaultFormat = (value: any) => [].concat(value).join('、');

export const DrawerFormField: FC<IDrawerFormFieldProps> = ({
  value,
  visible,
  className,
  title,
  children,
  removeSearchVal,
  onChange,
  onClose,
  previewFmt = defaultFormat,
  isSearch,
  headerSlot,
}) => {
  const [local, setLocal] = useState<string[]>([]);
  const eleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const close: React.MouseEventHandler<HTMLSpanElement> = useCallback(
    (e) => {
      e.stopPropagation();
      onClose();
    },
    [onClose],
  );

  const commit: React.MouseEventHandler<HTMLSpanElement> = useCallback(
    (e) => {
      onChange?.(local);
      close(e);
    },
    [local, close],
  );

  const cancel: React.MouseEventHandler<HTMLSpanElement> = useCallback(
    (e) => {
      e.stopPropagation();
      setLocal(value);
      onClose();
      // 点击取消按钮会传一个空值，用来清空搜索框的值
      removeSearchVal?.('');
    },
    [setLocal, value, close],
  );

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        (eleRef.current?.querySelector('input, textarea') as HTMLInputElement)?.focus();
      }, 300);
    }
  }, [visible]);

  return (
    <>
      {previewFmt(value)}
      <Drawer visible={visible} className={classNames('drawer-selector', className)}>
        {headerSlot}
        {!headerSlot && (
          <div className="title">
            {isSearch ? (
              <>
                <span></span>
                <span className="cancel" onClick={cancel} style={{ color: '#006FFF' }}>
                  取消
                </span>
              </>
            ) : (
              <>
                <span className="cancel" onClick={cancel}>
                  取消
                </span>
                <span>{title}</span>
                <span className="confirm" onClick={commit}>
                  完成
                </span>
              </>
            )}
          </div>
        )}
        <div ref={eleRef}>
          {children ? cloneElement(children, { value: local, onChange: setLocal }) : null}
        </div>
      </Drawer>
    </>
  );
};

export default FormField;
